// hooks/sale/useSaleDispatcher.ts
import { useSale } from './useSale';
import { useCreditFiscalSale } from './useCreditFiscalSale';
import { TaxType } from '../../types/sale/TaxType';
import { useConsumerFinalSale } from './useConsumerFinalSale';
import { useCreditNoteSale } from './useCreditNoteSale';
import { useExportInvoiceSale } from './useExportInvoiceSale';
import { DOCUMENT_TYPE_CODES } from '../../constants/documentTypeCodes';

export const useSaleDispatcher = () => {
  const { form } = useSale();
  const creditFiscal = useCreditFiscalSale();
  const invoice = useConsumerFinalSale();
  const creditNote = useCreditNoteSale();
  const exportInvoice = useExportInvoiceSale();

  const documentTypeCode = form.documentType?.code;

  const addProduct = (payload: {
    saleId: number;
    productId: number;
    quantity: number;
    unitPrice: number;
    taxType: TaxType;
    creditFiscalId?: number;
  }) => {
    switch (documentTypeCode) {
      case DOCUMENT_TYPE_CODES.TAX_CREDIT:
        return creditFiscal.handlerAddCreditFiscalProduct({
          ...payload,
          creditFiscalId: undefined,
        });

      case DOCUMENT_TYPE_CODES.INVOICE:
        return invoice.handlerAddConsumerFinalProduct({
          ...payload,
          creditFiscalId: undefined,
        });

      case DOCUMENT_TYPE_CODES.CREDIT_NOTE:
        return creditNote.handlerAddCreditNoteProduct(payload);

      case DOCUMENT_TYPE_CODES.EXPORT_INVOICE:
        return exportInvoice.handlerAddExportInvoiceProduct({
          ...payload,
          creditFiscalId: undefined,
        });

      default:
        console.warn('❌ Documento no soportado aún:', documentTypeCode);
    }
  };

  const updateProduct = (updated: {
    saleId: number;
    productId: number;
    quantity: number;
    unitPrice: number;
    taxType: TaxType;
    detailId: number;
    creditFiscalId?: number;
    creditFiscalDetailId?: number;
  }) => {
    switch (documentTypeCode) {
      case DOCUMENT_TYPE_CODES.TAX_CREDIT:
        return creditFiscal.handlerUpdateCreditFiscalProduct({
          ...updated,
          creditFiscalId: undefined,
          creditFiscalDetailId: undefined,
        });

      case DOCUMENT_TYPE_CODES.INVOICE:
        return invoice.handlerUpdateConsumerFinalProduct({
          ...updated,
          creditFiscalId: undefined,
          creditFiscalDetailId: undefined,
        });

      case DOCUMENT_TYPE_CODES.CREDIT_NOTE:
        return creditNote.handlerUpdateCreditNoteProduct(updated);

      case DOCUMENT_TYPE_CODES.EXPORT_INVOICE:
        return exportInvoice.handlerUpdateExportInvoiceProduct({
          ...updated,
          creditFiscalId: undefined,
          creditFiscalDetailId: undefined,
        });

      default:
        console.warn('❌ Documento no soportado aún:', documentTypeCode);
    }
  };

  const deleteProduct = (productId: number) => {
    switch (documentTypeCode) {
      case DOCUMENT_TYPE_CODES.TAX_CREDIT:
        return creditFiscal.handlerDeleteCreditFiscalProduct(productId);

      case DOCUMENT_TYPE_CODES.INVOICE:
        return invoice.handlerDeleteConsumerFinalProduct(productId);

      case DOCUMENT_TYPE_CODES.CREDIT_NOTE:
        return creditNote.handlerDeleteCreditNoteProduct(productId);

      case DOCUMENT_TYPE_CODES.EXPORT_INVOICE:
        return exportInvoice.handlerDeleteExportInvoiceProduct(productId);
    }
  };

  const getProductById = (detailId: number) => {
    switch (documentTypeCode) {
      case DOCUMENT_TYPE_CODES.TAX_CREDIT:
        return creditFiscal.handlerGetProductById(detailId);

      case DOCUMENT_TYPE_CODES.INVOICE:
        return invoice.handlerGetProductById(detailId);

      case DOCUMENT_TYPE_CODES.CREDIT_NOTE:
        return creditNote.handlerGetProductById(detailId);

      case DOCUMENT_TYPE_CODES.EXPORT_INVOICE:
        return exportInvoice.handlerGetProductById(detailId);

      default:
        console.warn('❌ Documento no soportado para obtener producto:', documentTypeCode);
    }
  };

  return {
    addProduct,
    updateProduct,
    deleteProduct,
    getProductById,
  };
};
